import React from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContextDrawerTabs from './ContextDrawerTabs';
import ContextDrawerTabPanel from './ContextDrawerTabPanel';
import ProfileTab from './tabs/ProfileTab';
import OverviewTab from './tabs/OverviewTab';
import LeadDetailsTab from './tabs/LeadDetailsTab';
import NotesTab from './tabs/NotesTab';
import AISummaryTab from './tabs/AISummaryTab';
import useInterveneSx from '../../hooks/useInterveneSx';
import { CONTEXT_DRAWER_TABS } from '../../constants/contextDrawerTabs';
import { getConversationPhone } from '../../utils/conversationIdentity';
import { useRecoilValue } from 'recoil';
import { interveneAiSummaryAtom } from '../../state/interveneAtoms';

function DrawerHeader({ onClose, styles }) {
  return (
    <Box sx={styles.header}>
      <Box sx={styles.headerRow}>
        <Typography sx={styles.headerTitle}>Customer Context</Typography>
        {onClose && (
          <IconButton onClick={onClose} aria-label="Close customer context" size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

function DrawerShell({
  activeUser,
  activeTab,
  onTabChange,
  onClose,
  journey,
  isLoadingProfile,
  isDrawerOpen,
  organisationId,
  showAiTab,
  leadFormContext,
  inline = false,
}) {
  const interveneSx = useInterveneSx();
  const styles = interveneSx.contextDrawer;
  const phone = getConversationPhone(activeUser);

  const shellSx = inline
    ? [styles.root, styles.inlineWidth]
    : [styles.root, { width: '100%' }];

  if (!phone) {
    return (
      <Box sx={shellSx}>
        <DrawerHeader onClose={onClose} styles={styles} />
        <Box sx={styles.emptyShell}>
          <Typography variant="body2">Select a conversation to view context.</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={shellSx}>
      <DrawerHeader onClose={onClose} styles={styles} />

      <ContextDrawerTabs
        activeTab={activeTab}
        onTabChange={onTabChange}
        showAiTab={showAiTab}
      />

      <ContextDrawerTabPanel tabId={CONTEXT_DRAWER_TABS.PROFILE} activeTab={activeTab}>
        <ProfileTab
          activeUser={activeUser}
          journey={journey}
          isLoadingProfile={isLoadingProfile}
        />
      </ContextDrawerTabPanel>

      <ContextDrawerTabPanel tabId={CONTEXT_DRAWER_TABS.OVERVIEW} activeTab={activeTab}>
        <OverviewTab
          activeUser={activeUser}
          isDrawerOpen={isDrawerOpen}
          organisationId={organisationId}
          onLeadSelect={leadFormContext?.handleLeadSelect}
        />
      </ContextDrawerTabPanel>

      <ContextDrawerTabPanel tabId={CONTEXT_DRAWER_TABS.LEAD} activeTab={activeTab} fillHeight>
        <LeadDetailsTab
          phone={phone}
          isDrawerOpen={isDrawerOpen && activeTab === CONTEXT_DRAWER_TABS.LEAD}
          organisationId={organisationId}
          leadFormContext={leadFormContext}
        />
      </ContextDrawerTabPanel>

      <ContextDrawerTabPanel tabId={CONTEXT_DRAWER_TABS.NOTES} activeTab={activeTab}>
        <NotesTab isDrawerOpen={isDrawerOpen && activeTab === CONTEXT_DRAWER_TABS.NOTES} />
      </ContextDrawerTabPanel>

      {showAiTab && (
        <ContextDrawerTabPanel tabId={CONTEXT_DRAWER_TABS.AI} activeTab={activeTab}>
          <AISummaryTab isDrawerOpen={isDrawerOpen && activeTab === CONTEXT_DRAWER_TABS.AI} />
        </ContextDrawerTabPanel>
      )}
    </Box>
  );
}

export default function CustomerContextDrawer({
  open,
  activeTab,
  onTabChange,
  onClose,
  activeUser,
  journey,
  isLoadingProfile,
  isInlineLayout,
  organisationId,
  leadFormContext,
}) {
  const styles = useInterveneSx().contextDrawer;
  const aiSummary = useRecoilValue(interveneAiSummaryAtom);
  const showAiTab = Boolean(activeUser?.phone_number);

  const shellProps = {
    activeUser,
    activeTab,
    onTabChange,
    onClose,
    journey,
    isLoadingProfile,
    isDrawerOpen: open,
    organisationId,
    showAiTab: showAiTab || Boolean(aiSummary),
    leadFormContext,
  };

  if (isInlineLayout) {
    if (!open) return null;
    return <DrawerShell {...shellProps} inline />;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: styles.overlayPaper }}
    >
      <DrawerShell {...shellProps} />
    </Drawer>
  );
}
